import {inject, injectable} from "inversify";
import {GroundSlot} from "@/modules/club-grounds/entity";
import {BankCategory} from "@/modules/finances/enums";
import {EventEmitter} from "@/modules/base/events";
import {AfterGroundSlotUnlockedEvent} from "@/modules/club-grounds/events/AfterGroundSlotUnlockedEvent";
import {Season} from "@/modules/season/entity";
import {BeforeGroundSlotDemolishedEvent} from "@/modules/club-grounds/events/BeforeGroundSlotDemolishedEvent";
import {AfterGroundSlotDemolishedEvent} from "@/modules/club-grounds/events/AfterGroundSlotDemolishedEvent";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import GateManager from "@/modules/action-points/services/GateManager";
import {GateTypeEnum} from "@/modules/action-points/enums";

@injectable()
export default class SlotDemolisher {
  constructor(
    @inject(GateManager) private gateManager: GateManager,
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
  }

  public async finish(slot: GroundSlot) {
    await this.em.emitAsync(new BeforeGroundSlotDemolishedEvent(slot));

    slot.demolishAt = null;
    slot.type = null;
    await slot.save();

    await this.em.emitAsync(new AfterGroundSlotDemolishedEvent(slot));
  }

  public async cancel(slot: GroundSlot) {
    slot.demolishAt = null;
    await slot.save();
  }

  public async queue(slot: GroundSlot) {
    if (slot.demolishAt) {
      throw 'Ground slot demolition is queued already.';
    }

    if (!slot.demolishable) {
      throw 'Ground slot is not demolishable.';
    }

    const season = await Season.current();
    const duration = this.configRepository.get('clubGrounds.durations.demolish')(slot);
    const price = this.configRepository.get('clubGrounds.costs.demolish')(slot);

    await this.gateManager.getGate(GateTypeEnum.BALANCE).currency.subtract(
      price,
      {
        category: BankCategory.CLUB_GROUNDS,
        label: translator.t('demolish_club_grounds'),
      }
    );

    slot.demolishAt = season.currentDate.add(duration, 'days').toDate();
    await slot.save();

    await this.em.emitAsync(new AfterGroundSlotUnlockedEvent(slot));
  }
}
